import React, { useEffect, useState } from "react";
import { Alert, Button, Space } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { getZatcaDevices } from "../../network/network";
import {
  ZATCA_CSID_DAYS_AMBER_MIN,
  ZATCA_CSID_DAYS_RED,
  ZatcaDeviceStatus,
} from "../../configs/constants";
import { handleApiError } from "../../utils/errorHandler";

const ZatcaCsidExpiryBanner = ({ blocking = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let isEffectActive = true;
    setIsLoading(true);
    getZatcaDevices()
      .then((response) => {
        if (!isEffectActive) return;
        if (response?.data?.success === false) {
          handleApiError(response, t);
          setDevices([]);
          return;
        }

        const payload = response?.data;
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.data?.devices)
              ? payload.data.devices
              : [];
        setDevices(list);
      })
      .catch((error) => {
        if (isEffectActive) {
          handleApiError(error, t);
          setDevices([]);
        }
      })
      .finally(() => {
        if (isEffectActive) setIsLoading(false);
      });
    return () => {
      isEffectActive = false;
    };
  }, [t]);

  if (isLoading || !devices.length) return null;

  const affected = devices
    .map((device) => {
      const branchName = device?.branch?.name || device?.device_name || "—";
      const expiry = device?.production_expires_at
        ? moment(device.production_expires_at)
        : null;
      const daysLeft = expiry?.isValid() ? expiry.diff(moment(), "days") : null;

      const hardExpired =
        device?.status === ZatcaDeviceStatus.CsidExpired ||
        (daysLeft !== null && daysLeft < 0);

      let level = null;
      if (device?.requires_reonboarding_at) level = "reonboarding";
      else if (hardExpired) level = "expired";
      else if (daysLeft !== null && daysLeft < ZATCA_CSID_DAYS_RED)
        level = "red";
      else if (
        daysLeft !== null &&
        daysLeft >= ZATCA_CSID_DAYS_RED &&
        daysLeft <= ZATCA_CSID_DAYS_AMBER_MIN * 4
      )
        level = "amber";

      return { device, branchName, daysLeft, level };
    })
    .filter((row) => row.level !== null);

  if (!affected.length) return null;

  const visible = blocking
    ? affected.filter((row) => row.level === "expired")
    : affected;
  if (!visible.length) return null;

  const renewAction = (device) => (
    <Button
      type={
        device?.status === ZatcaDeviceStatus.CsidExpired
          ? "text"
          : "default"
      }
      danger={device?.status === ZatcaDeviceStatus.CsidExpired}
      onClick={() => navigate("/zatca-onboarding")}
    >
      {t("renew_now")}
    </Button>
  );

  return (
    <Space
      direction="vertical"
      size="small"
      style={{ width: "100%", marginBottom: 16 }}
    >
      {visible.map(({ device, branchName, daysLeft, level }) => {
        let type = "warning";
        let message = null;

        if (level === "reonboarding") {
          type = "warning";
          message = t("zatca_reonboarding_required_for_branch", {
            branch: branchName,
            reason:
              device?.stale_reason || t("zatca_reonboarding_required_helper"),
          });
        } else if (level === "expired") {
          type = "error";
          const days = daysLeft === null ? 0 : Math.abs(daysLeft);
          if (blocking) {
            message = (
              <div>
                <div style={{ fontWeight: 600 }}>
                  {t("zatca_csid_banner_blocking_title")}
                </div>
                <div style={{ marginTop: 4 }}>
                  {t("zatca_csid_banner_blocking_helper")}
                </div>
              </div>
            );
          } else {
            message = t("zatca_csid_banner_expired", {
              count: days,
              days,
              branch: branchName,
              defaultValue:
                "{{branch}} certificate expired {{days}} days ago. Invoices from this branch cannot be reported to ZATCA until renewed.",
            });
          }
        } else if (level === "red") {
          type = "error";
          message = t("zatca_csid_banner_expiring_red", {
            days: daysLeft,
            branch: branchName,
          });
        } else if (level === "amber") {
          type = "warning";
          message = t("zatca_csid_banner_expiring_amber", {
            days: daysLeft,
            branch: branchName,
          });
        }

        return (
          <Alert
            key={device.id}
            type={type}
            showIcon
            closable={level !== "expired"}
            message={message}
            action={renewAction(device)}
          />
        );
      })}
    </Space>
  );
};

export default ZatcaCsidExpiryBanner;
